// @flow

import * as React from 'react';
import { FormControl, FormHelperText, Link } from '@material-ui/core';
import { Box, Flex } from 'react-system';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useFormik, Form } from '@realadvisor/form';
import { useCaptcha } from '@realadvisor/hooks';
import { requestPhoneCode } from '@realadvisor/auth-client';
import { getAnotherAppOrigin } from '@realadvisor/host-resolver';
import { collectMarketingSignals } from '@realadvisor/marketing-signals/browser';
import { useTheme } from '../theme';
import { useLocale } from '../hooks/locale';
import { useUserData } from '../hooks/user-data';
import { useAnalytics } from '../controls/analytics/index';
import { PhoneInput, usePhone } from '../controls/phone-input';
import { LoadingButton } from '../controls/loading-button';
import { PinInput } from '../controls/PinInput';
import { InlineMarked } from '../controls/marked';
import { useSeoUserInfo } from './seo-user-info';
import type { phoneVerification_root$key } from './__generated__/phoneVerification_root.graphql';
import type { phoneVerificationUpdateLeadMutation } from './__generated__/phoneVerificationUpdateLeadMutation.graphql';
import type { phoneVerificationCheckMutation } from './__generated__/phoneVerificationCheckMutation.graphql';

// 60s while HighConnexion is paused from routing (2026-08-14, bill
// negotiation): these public funnels send `auth_code_${appName}`
// (Twilio-eligible), so on the paused two-provider stack the first resend
// starts on Twilio — the expensive route — and a premature resend costs money.
// Matches CRM's ResendCodeButton and valuation-studio. Restore 30s when HC
// returns to the stack.
const RESEND_COOLDOWN_SECONDS = 60;

const dateStringToNumber = (dateString?: null | string): number => {
  if (dateString != null) {
    return new Date(dateString).getTime();
  }
  return 0;
};

const collectMarketingMetadata = (
  seoUserInfo: ?{
    +landing_page_url?: ?string,
    +referrer?: ?string,
    ...
  },
) => {
  const { searchParams } = new URL(window.location.href);
  return {
    user_agent: navigator.userAgent,
    // Use seoUserInfo from cookies (captures original landing page/referrer)
    referrer: seoUserInfo?.referrer ?? null,
    landing_page_url: seoUserInfo?.landing_page_url ?? null,
    utm_source: searchParams.get('utm_source'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_medium: searchParams.get('utm_medium'),
    // Full marketing-signals payload, collected at submit time; the auth
    // service enriches + sanitizes it before insert.
    marketing_signals: collectMarketingSignals(),
  };
};

type Props = {|
  root?: null | phoneVerification_root$key,
  leadId?: string,
  primary?: boolean,
  developmentRegistrationFormId?: string,
  onComplete: () => void,
|};

const VerificationCheckForm = ({
  leadId,
  developmentRegistrationFormId,
  number,
  onComplete,
  onCancel,
}) => {
  const { t } = useLocale();
  const { track } = useAnalytics();
  const { colors } = useTheme();
  const seoUserInfo = useSeoUserInfo();

  const [checkCode, checking] = useMutation<phoneVerificationCheckMutation>(
    graphql`
      mutation phoneVerificationCheckMutation($input: PhoneSignInCheckInput!) {
        phoneSignInCheck(input: $input) {
          errors {
            code
          }
        }
      }
    `,
  );

  const [error, setError] = React.useState(null);
  const [didNotReceiveTheCode, setDidNotReceiveTheCode] = React.useState(false);
  const [captchaSolved, setCaptchaSolved] = React.useState(false);
  const { reCaptchaKeyV2, reCaptchaKey } = useUserData();
  const captcha = useCaptcha({
    reCaptchaKeyV2,
    reCaptchaKeyV3: reCaptchaKey,
    onSolved: () => setCaptchaSolved(true),
  });
  // Armed from mount, not 0: this form renders right after the initial code
  // was requested by the parent, so starting at 0 would leave the resend
  // button enabled immediately — and the first resend is exactly the one that
  // seeds provider_attempt=1 and lands on Twilio on the paused stack. Same
  // rationale as CRM's ResendCodeButton.
  const [timeLeft, setTimeLeft] = React.useState(RESEND_COOLDOWN_SECONDS);
  const [
    numberOfTimesTheCodeWasRequested,
    setNumberOfTimesTheCodeWasRequested,
  ] = React.useState(0);
  const [supportNotified, setSupportNotified] = React.useState(false);

  const sendNotification = async (leadId: string) => {
    const apiUrl = getAnotherAppOrigin(location.origin, 'services');
    const request = {
      method: 'POST',
      credentials: 'include',
    };
    return fetch(`${apiUrl}/api/notify?leadId=${leadId}`, request).then(
      response => {
        if (response.ok === false) {
          throw Error(response.statusText);
        }
      },
    );
  };
  const NotifySupport = () => {
    if (leadId != null) {
      sendNotification(leadId);
    }
    setSupportNotified(true);
  };
  // Depend on `timeLeft`: the timer now arms from mount and runs for a full
  // 60s on every user who reaches the code screen, so a no-deps effect would
  // tear down and rebuild the 1s interval on every render — a render inside a
  // tick delays the decrement, and a sustained render loop would stall it,
  // leaving the button stuck disabled. Keyed on `timeLeft`, one interval lives
  // per second.
  React.useEffect(() => {
    if (timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timeLeft]);
  const [requesting, setRequesting] = React.useState(false);

  const handleRequestCode = async recaptchaToken => {
    setRequesting(true);
    try {
      const response = await requestPhoneCode({
        number,
        recaptchaToken,
        marketingMetadata: collectMarketingMetadata(seoUserInfo),
      });
      if (response.ok === false) {
        throw new Error(response.statusText);
      }
      const json = await response.json();
      if (json.hint != null) {
        console.info(json.hint);
        alert(json.hint);
      }
      track('RA.Web.SMSVerification.Sent', { number });
      setNumberOfTimesTheCodeWasRequested(numberOfTimesTheCodeWasRequested + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('captcha')) {
        captcha.verifyUser();
      } else {
        setError(message);
      }
    } finally {
      setRequesting(false);
      // Restart the cooldown on every outcome, not just success. A failure —
      // notably auth's 429 TOO_MANY_REQUESTS — otherwise leaves timeLeft at 0
      // and re-enables the button at click speed, making this the one client
      // able to poll auth's throttle. Nothing was dispatched on a failure, so
      // this isn't a Twilio-cost hole, but it matches CRM/valuation-studio,
      // which both restart on refusal. The 3-attempt cap still only counts
      // successes (the increment stays in the try above).
      setTimeLeft(RESEND_COOLDOWN_SECONDS);
    }
  };

  const handleVerifyCode = (code: string) => {
    checkCode({
      variables: {
        input: {
          leadId: leadId ?? null,
          developmentRegistrationFormId: developmentRegistrationFormId ?? null,
          number,
          code,
        },
      },
      onCompleted: response => {
        const errorCode = response.phoneSignInCheck?.errors?.code;
        if (errorCode != null) {
          track('RA.Web.SMSVerification.Error', { number });
          setError(errorCode);
        } else {
          track('RA.Web.SMSVerification.Success', { number });
          onComplete();
        }
      },
      onError: () => {
        track('RA.Web.SMSVerification.Error', { number });
      },
    });
  };

  return (
    <>
      <Box mb={3}>
        <Box css={{ textAlign: 'center', color: colors.green500 }} mb={2}>
          {t('enterTheCodeReceivedBySMS')}
        </Box>
        <PinInput
          // reset code on error
          key={error}
          disabled={checking}
          length={4}
          onChange={code => {
            handleVerifyCode(code);
          }}
        />
        {error != null && (
          <FormHelperText error={true} filled={true}>
            {error}
          </FormHelperText>
        )}
      </Box>
      {didNotReceiveTheCode === false ? (
        <Box css={{ textAlign: 'center' }}>
          <Link
            underline="always"
            onClick={() => {
              setDidNotReceiveTheCode(true);
              // Delay to ensure captcha.element is rendered before verifyUser
              setTimeout(() => captcha.verifyUser(), 0);
            }}
            style={{ cursor: 'pointer' }}
          >
            <InlineMarked>{t('didNotReceiveTheCode')}</InlineMarked>
          </Link>
        </Box>
      ) : (
        <Box
          css={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box mb={3}>
            <p>{t('pleaseVerifyYourPhoneNumber')}</p>
            <p style={{ fontWeight: 'bold' }}>{number}</p>
            <Link
              underline="always"
              onClick={() => onCancel()}
              style={{ cursor: 'pointer' }}
            >
              {t('editPhoneNumber')}
            </Link>
          </Box>
          <Flex mb={3} justifyContent="center">
            {captcha.element}
          </Flex>
          <Box mb={3}>
            {numberOfTimesTheCodeWasRequested < 3 ? (
              <LoadingButton
                size="medium"
                delay={1000}
                loading={requesting}
                disabled={timeLeft !== 0 || !captchaSolved}
                onClick={() => {
                  captcha.requestToken(token => {
                    handleRequestCode(token);
                  });
                }}
              >
                {timeLeft !== 0
                  ? t('pleaseWaitBeforeRetrying', {
                      seconds: timeLeft.toString(),
                    })
                  : t('reSendSMSVerificationCode')}
              </LoadingButton>
            ) : (
              <>
                <p style={{ color: 'red', fontWeight: 'bold' }}>
                  {t('attemptsUnsuccessful', { numberOfAttemps: 3 })}
                </p>
                <p>{t('stillHaventReceivedTheSmsCode')}</p>
                <p>{t('pleaseLetUsKnow')}</p>
              </>
            )}
          </Box>
          {supportNotified ? (
            <>
              <p style={{ color: 'green', fontWeight: 'bold' }}>
                {t('supportHasBeenNotified')}
              </p>
              <p>
                <InlineMarked>{t('youWillNotLooseYourValuation')}</InlineMarked>
              </p>
              <p style={{ marginBottom: '4px' }}>
                {t('weApologiseForTheInconvenience')}
              </p>
              <span>{t('theRealAdvisorTeam')}</span>
            </>
          ) : (
            <LoadingButton
              variant="outlined"
              size="medium"
              delay={1000}
              loading={checking}
              onClick={() => NotifySupport()}
            >
              <InlineMarked>{t('stillNotWorkingGetHelp')}</InlineMarked>
            </LoadingButton>
          )}
        </Box>
      )}
    </>
  );
};

export const PhoneVerification = (props: Props): React.Node => {
  const root = useFragment(
    graphql`
      fragment phoneVerification_root on Query {
        me {
          firstName
          lastName
          primaryEmail {
            email
          }
          phoneNumbers {
            primary
            formattedNumber
            verified
            createdAt
          }
        }
      }
    `,
    props.root,
  );

  const [verification, setVerification] = React.useState(false);
  const [requesting, setRequesting] = React.useState(false);
  const { t } = useLocale();
  const { track } = useAnalytics();
  const { reCaptchaKeyV2, reCaptchaKey } = useUserData();
  const [captchaSolved, setCaptchaSolved] = React.useState(false);
  const captcha = useCaptcha({
    reCaptchaKeyV2,
    reCaptchaKeyV3: reCaptchaKey,
    onSolved: () => setCaptchaSolved(true),
  });
  const { initialPhonePrefix } = usePhone();
  const seoUserInfo = useSeoUserInfo();

  // Show v2 captcha immediately on mount
  React.useEffect(() => {
    captcha.verifyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [updateLead] = useMutation<phoneVerificationUpdateLeadMutation>(
    graphql`
      mutation phoneVerificationUpdateLeadMutation($lead: LeadInput!) {
        upsertLead(input: { lead: $lead }) {
          __typename
        }
      }
    `,
  );

  // In case no number is marked as primary the pre-filled number should be:
  // 1. phone number which was last marked verified in case of multiple verified phone numbers
  // 2. latest number added in case of no verified numbers

  const primaryNumber = root?.me?.phoneNumbers.find(elem => elem.primary);
  const verifiedNumbers = root?.me?.phoneNumbers
    .filter(elem => elem.verified)
    .sort(
      (numA, numB) =>
        dateStringToNumber(numB.createdAt) - dateStringToNumber(numA.createdAt),
    );
  const notVerifiedNumbers = root?.me?.phoneNumbers
    .filter(elem => !elem.verified)
    .sort(
      (numA, numB) =>
        dateStringToNumber(numB.createdAt) - dateStringToNumber(numA.createdAt),
    );

  let prefillNumber = primaryNumber?.formattedNumber;
  if (prefillNumber == null && verifiedNumbers?.length !== 0) {
    prefillNumber = verifiedNumbers?.[0].formattedNumber;
  }
  if (prefillNumber == null && notVerifiedNumbers?.length !== 0) {
    prefillNumber = notVerifiedNumbers?.[0].formattedNumber;
  }
  if (prefillNumber == null) {
    prefillNumber = initialPhonePrefix;
  }

  const form = useFormik({
    initialValues: {
      phone: prefillNumber,
    },
    validate: () => ({}),
    onSubmit: values => {
      const verifyAndRequestCode = () => {
        captcha.requestToken(recaptchaToken => {
          (async () => {
            setRequesting(true);
            try {
              const response = await requestPhoneCode({
                number: values.phone,
                recaptchaToken,
                marketingMetadata: collectMarketingMetadata(seoUserInfo),
              });
              const json = await response.json();
              if (json.hint != null) {
                console.info(json.hint);
                alert(json.hint);
              }
              setVerification(true);
              track('RA.Web.SMSVerification.Sent', { number: values.phone });
            } catch (err) {
              const message = err instanceof Error ? err.message : '';
              if (message.toLowerCase().includes('captcha')) {
                captcha.verifyUser();
              } else if (
                message.includes('not valid') ||
                message.includes('Invalid')
              ) {
                form.setResponseErrors({ phone: t('phoneNumberIsNotValid') });
              } else {
                form.setResponseErrors({ phone: message });
              }
            } finally {
              setRequesting(false);
            }
          })();
        });
      };
      if (props.leadId != null) {
        updateLead({
          variables: {
            lead: {
              id: props.leadId,
              firstName: root?.me?.firstName,
              lastName: root?.me?.lastName,
              emailAddress: root?.me?.primaryEmail?.email,
              phoneNumber: values.phone,
            },
          },
          onCompleted: () => verifyAndRequestCode(),
          onError: (error: any) => {
            if (error.errors[0].message === 'phoneNumber is not valid') {
              form.setResponseErrors({
                phone: t('phoneNumberIsNotValid'),
              });
            }
          },
        });
      } else {
        verifyAndRequestCode();
      }
    },
  });

  return verification ? (
    <VerificationCheckForm
      leadId={props.leadId}
      developmentRegistrationFormId={props.developmentRegistrationFormId}
      number={form.values.phone}
      onComplete={props.onComplete}
      onCancel={() => setVerification(false)}
    />
  ) : (
    <Form onSubmit={form.submitForm}>
      <Box mb={3}>
        <FormControl
          variant="outlined"
          disabled={requesting}
          error={form.errors.phone != null}
        >
          <PhoneInput
            value={form.values.phone}
            onChange={phone => form.setValues({ phone })}
            onBlur={() => form.setTouched({ phone: true })}
          />
          {form.errors.phone != null && (
            <FormHelperText>{form.errors.phone}</FormHelperText>
          )}
        </FormControl>
      </Box>
      {captcha.element !== false && (
        <Flex justifyContent="center" mb={3}>
          {captcha.element}
        </Flex>
      )}
      <LoadingButton
        size="medium"
        loading={requesting}
        disabled={!captchaSolved}
        type="submit"
      >
        {t('requestSMSVerificationCode')}
      </LoadingButton>
    </Form>
  );
};
